import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/* A new page starts at the top.
 *
 * React Router swaps the page under the SAME document, so the window keeps whatever
 * scroll offset the previous page had: a link clicked halfway down the collectibles
 * page landed halfway down the TCG page, with its hero out of view. A handful of pages
 * (the raffle payment flow, the giveaway offer) worked around it with their own
 * `window.scrollTo(0, 0)`; this does it once, for every route, from inside the router.
 *
 * Only a NEW page scrolls:
 * - back/forward (POP) is left to the browser, which restores the position the reader
 *   had on that page;
 * - a change to the query string alone (the catalog's ?q=&rarity=&type=, utm tags) is
 *   not a new page — the effect keys on the pathname, not the search;
 * - a hash navigation (#section) is the anchor's business, not ours.
 *
 * The navigation type is read through a ref rather than listed as a dependency: a
 * query-string update is a REPLACE on the same pathname, and with the type in the
 * dependency list that change re-ran the effect and threw the catalog back to the
 * top on every keystroke in its search box. The initial load reports POP, so a reload
 * keeps the browser's own restored position too.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const navigationTypeRef = useRef(navigationType);
  navigationTypeRef.current = navigationType;

  useEffect(() => {
    if (navigationTypeRef.current === 'POP' || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
